import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    InlineLoading,
    Modal,
    NotificationAlert,
    Tree,
} from "src/components/common";
import { ITree } from "src/components/common/Tree/Tree";
import { SelectFormInput } from "src/components/form";
import { ModalSize } from "src/enumerations/ModalSize";
import { TrialGroupsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { TrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialActions from "../../../actions/trial";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import { TrialLayout } from "../../index";

import "./TrialGroupsPage.scss";

interface TrialGroupsPageProps {
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    trialGroups: Dtos.TrialGroup[];
    loadingTrialGroups: boolean;
    loadTrialGroupsSuccess: boolean;
    loadTrialGroupsFailure: boolean;
    loadTrialGroupsErrorCode: string;
    trialInstitutions: Dtos.TrialInstitution[];
    loadingTrialInstitutions: boolean;
    loadTrialInstitutionsSuccess: boolean;
    loadTrialInstitutionsFailure: boolean;
    loadTrialInstitutionsErrorCode: string;
    groups: Dtos.Group[];
    loadingGroups: boolean;
    loadGroupsSuccess: boolean;
    loadGroupsFailure: boolean;
    loadGroupsErrorCode: string;
    savingTrialGroup: boolean;
    saveTrialGroupSuccess: boolean;
    saveTrialGroupFailure: boolean;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadGroups: GroupActions.IGroupLoadActionCreator;
    clearGroups: GroupActions.IGroupClearActionCreator;
    loadTrialGroups: TrialGroupActions.ITrialGroupsLoadByTrialIdActionCreator;
    clearTrialGroups: TrialGroupActions.ITrialGroupClearActionCreator;
    saveTrialGroup: TrialGroupActions.ITrialGroupSaveActionCreator;
    loadTrialInstitutions: TrialInstitutionActions.ITrialInstitutionsLoadByTrialIdActionCreator;
    clearTrialInstitutions: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialGroupsPage: FC<TrialGroupsPageProps> = ({
    trial,
    loadingTrial,
    loadTrialSuccess,
    loadTrialFailure,
    loadTrialErrorCode,
    trialGroups,
    loadingTrialGroups,
    loadTrialGroupsSuccess,
    loadTrialGroupsFailure,
    loadTrialGroupsErrorCode,
    trialInstitutions,
    loadingTrialInstitutions,
    loadTrialInstitutionsSuccess,
    loadTrialInstitutionsFailure,
    loadTrialInstitutionsErrorCode,
    groups,
    loadingGroups,
    loadGroupsSuccess,
    loadGroupsFailure,
    loadGroupsErrorCode,
    permissions,
    loadTrial,
    clearTrial,
    loadGroups,
    clearGroups,
    loadTrialGroups,
    clearTrialGroups,
    saveTrialGroup,
    loadTrialInstitutions,
    clearTrialInstitutions,
    setBreadcrumbs,
    setDrawerItems,
    savingTrialGroup,
    saveTrialGroupSuccess
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [addTrialGroupId, setAddTrialGroupId] = useState<number | undefined>(
        undefined
    );
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Variables --
    const showTrialGroups =
        !loadingTrial &&
        loadTrialSuccess &&
        !loadingGroups &&
        loadGroupsSuccess &&
        !loadingTrialGroups &&
        loadTrialGroupsSuccess &&
        !loadingTrialInstitutions &&
        loadTrialInstitutionsSuccess &&
        !creatingTree;

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load trial
    useEffect(() => {
        if (trialIdNumber && !trial) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialGroupsBreadcrumbs(trialIdNumber));

            setTreeData(null);

            loadTrial(trialIdNumber);
            loadTrialGroups(trialIdNumber);
            loadTrialInstitutions(trialIdNumber);
            loadGroups();
        }
    }, [
        trialIdNumber,
        trial,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadTrial,
        setTreeData,
        loadGroups,
        loadTrialGroups,
        loadTrialInstitutions
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadGroupsFailure) {
            navigate(`/error/${loadGroupsErrorCode}`);
        }
        if (loadTrialGroupsFailure) {
            navigate(`/error/${loadTrialGroupsErrorCode}`);
        }
        if (loadTrialInstitutionsFailure) {
            navigate(`/error/${loadTrialInstitutionsErrorCode}`);
        }
    }, [
        loadTrialFailure,
        loadGroupsFailure,
        loadTrialErrorCode,
        loadGroupsErrorCode,
        loadTrialGroupsFailure,
        loadTrialGroupsErrorCode,
        loadTrialInstitutionsFailure,
        loadTrialInstitutionsErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trial?.displayName) {
            setBreadcrumbs(
                TrialGroupsBreadcrumbs(trialIdNumber, trial.displayName)
            );
        }
    }, [trial, trialIdNumber, permissions, setDrawerItems, setBreadcrumbs]);

    // Create tree
    useEffect(() => {
        if (trialGroups && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial groups return
            if (trialGroups.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createInstitutionNodes = (institutions) =>
                institutions
                    ?.sort((a, b) =>
                        a.institution?.name.localeCompare(b.institution?.name)
                    )
                    ?.map((z) => ({
                        id: `institution-${z.id}`,
                        data: z.institution?.name,
                        icon: FontAwesomeIcons.Duotone.INSTITUTION,
                        collapsible: false,
                    })) ?? [];

            const createSubGroupNodes = (parentId) => {
                const subGroups =
                    trialGroups
                        ?.filter((b) => b.parentId === parentId)
                        ?.sort((a, b) =>
                            a.group?.name.localeCompare(b.group?.name)
                        )
                        ?.map((c) => ({
                            id: `sub-group-${c.groupId}/${c.id}`,
                            data: c.group?.name,
                            icon: FontAwesomeIcons.Duotone.GROUP,
                            collapsible: true,
                            subTree: {
                                children: [
                                    ...createInstitutionNodes(
                                        trialInstitutions?.filter(
                                            (y) => y.groupId === c.groupId
                                        )
                                    ),
                                    ...createSubGroupNodes(c.groupId), // Recursive call for deeper levels
                                ],
                            },
                        })) ?? [];
                return subGroups;
            };

            let treeNodes =
                trialGroups
                    ?.filter((a) => a.parentId == null)
                    ?.sort((a, b) => a.group?.name.localeCompare(b.group?.name))
                    ?.map((x) => ({
                        id: `group-${x.groupId}/${x.id}`,
                        data: x.group?.name,
                        icon: FontAwesomeIcons.Duotone.GROUP,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createSubGroupNodes(x.groupId),
                                ...(trialGroups?.some(
                                    (b) => b.parentId === x.groupId
                                )
                                    ? []
                                    : createInstitutionNodes(
                                            trialInstitutions?.filter(
                                                (y) => y.groupId === x.groupId
                                            )
                                        )),
                            ],
                        },
                    })) ?? [];

            // Append institutions without groupId directly to treeNodes
            treeNodes = [
                ...treeNodes,
                ...createInstitutionNodes(
                    trialInstitutions?.filter((y) => y.groupId == null)
                ),
            ];

            // Initialize tree toggle
            const expanded = treeNodes
                .filter(
                    (x) =>
                        x.subTree &&
                        x.subTree.children.length > 0 &&
                        x.subTree.children.every(
                            (y) => !y.id.includes("institution")
                        )
                )
                .map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialGroups,
        trialInstitutions,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    // Cleanup effect to clear the trial on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearTrial();
            clearGroups();
            clearTrialGroups();
            clearTrialInstitutions();
        };
    }, [clearTrial, setTreeData, clearGroups, clearTrialGroups, clearTrialInstitutions]);

    // Reload trial after saving trial group
    useEffect(() => {
        if (!savingTrialGroup && saveTrialGroupSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial Group saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            loadTrial(trialIdNumber);
            loadTrialGroups(trialIdNumber);
        }
    }, [
        savingTrialGroup,
        saveTrialGroupSuccess,
        loadTrial,
        trialIdNumber,
        setTreeData,
        loadTrialGroups
    ]);

    // Update tree
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [trialGroups, trialInstitutions]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("institution") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialInstitutionView,
                        trial.id
                    )
                ) {
                    navigate(
                        `/trials/${trialIdNumber}/trialinstitution/${id.replace(
                            "institution-",
                            ""
                        )}/details`
                    );
                }
                if (
                    id.includes("group") &&
                    AuthenticationHelper.hasAnyPermission(permissions, [
                        Dtos.Permission.GroupView,
                    ])
                ) {
                    let idParts: string[] = id
                        .replace("sub-", "")
                        .replace("group-", "")
                        .split("/");

                    if (idParts.length < 2) {
                        Alert.error(
                            <NotificationAlert
                                alertContent={<span>Failed to load trial group</span>}
                                icon={FontAwesomeIcons.Solid.BAN}
                            />
                        );
                        return;
                    }

                    navigate(
                        `/trials/${trialIdNumber}/groups/${idParts[0]}/trialgroup/${idParts[1]}/details`
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, trialIdNumber, permissions, trial]
    );

    const handleModalClose = () => {
        setIsOpen(false);
        setAddTrialGroupId(undefined);
    };

    const handleModalSubmit = async () => {
        if (!addTrialGroupId || !trialIdNumber) {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error occurred saving the trial group</span>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
            return;
        }

        let trialGroup: Dtos.TrialGroup = {
            trialId: trialIdNumber,
            groupId: addTrialGroupId,
            active: true,
        } as Dtos.TrialGroup;
        await saveTrialGroup(trialGroup);

        setIsOpen(false);
    };

    const selectOptions = useMemo(() => {
        if (!trialGroups || !groups) return [];

        return groups
            ?.filter(
                (x) => !trialGroups?.map((y) => y.groupId).includes(x.id)
            )
            ?.map((x) => ({ value: x.id, label: x?.name }));
    }, [groups, trialGroups]);

    return (
        <TrialLayout
            loading={
                loadingTrial || 
                loadingGroups || 
                loadingTrialGroups ||
                loadingTrialInstitutions ||
                savingTrialGroup
            }
            permission={Dtos.Permission.TrialGroupView}
        >
            <div className="row">
                <div className="col-9 mb-2">
                    <h1>Trial Groups</h1>
                </div>
                <div className="col-3 text-right">
                    {trial?.id &&
                        AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialGroupCreate,
                            trial.id
                        ) && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => setIsOpen(true)}
                                >
                                    Add
                                </button>
                                <Modal
                                    open={isOpen}
                                    disableCloseOnOverlayClick={true}
                                    onClose={handleModalClose}
                                    size={ModalSize.Md}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            Add Trial Group
                                        </h5>
                                        <button
                                            type="button"
                                            className="close"
                                            aria-label="Close"
                                            onClick={handleModalClose}
                                        >
                                            <FontAwesomeIcon
                                                fixedWidth
                                                icon={
                                                    FontAwesomeIcons.Regular
                                                        .TIMES
                                                }
                                            />
                                        </button>
                                    </div>
                                    <div className="modal-body p-3">
                                        <div className="form-row pb-2">
                                            <div className="col-12">
                                                {selectOptions.length === 0 &&
                                                trialGroups?.length >
                                                    0 ? (
                                                    <span>
                                                        All available groups
                                                        have been added.
                                                    </span>
                                                ) : (
                                                    <SelectFormInput
                                                        loading={loadingGroups}
                                                        multi={false}
                                                        options={selectOptions}
                                                        clearable={true}
                                                        backspaceRemoves={false}
                                                        name={"addTrialGroupId"}
                                                        id={"addTrialGroupId"}
                                                        placeholder="Group"
                                                        onChange={function (
                                                            value: any,
                                                            _options?: any
                                                        ): any {
                                                            setAddTrialGroupId(
                                                                value !=
                                                                    undefined
                                                                    ? parseInt(
                                                                            value
                                                                        )
                                                                    : undefined
                                                            );
                                                        }}
                                                        value={addTrialGroupId}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-row pt-2">
                                            <div className="col-12 d-flex justify-content-end">
                                                <button
                                                    className="btn btn-primary mr-1"
                                                    disabled={
                                                        savingTrialGroup ||
                                                        (selectOptions.length ===
                                                            0 &&
                                                            trialGroups
                                                                ?.length > 0)
                                                    }
                                                    onClick={handleModalSubmit}
                                                >
                                                    Submit
                                                </button>
                                                <button
                                                    className="btn btn-outline-primary ml-1"
                                                    onClick={handleModalClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        )}
                </div>
            </div>
            {showTrialGroups ? (
                <>
                    {treeData && treeData.children.length > 0 ? (
                        <Tree
                            treeData={treeData}
                            onToggle={handleToggle}
                            toggleState={toggleState}
                        />
                    ) : (
                        <div className="card card-shadow p-3">
                            <div className="alert alert-info d-flex mb-0">
                                <div className="mr-2">
                                    <FontAwesomeIcon
                                        icon={
                                            FontAwesomeIcons.Solid.INFO_SQUARE
                                        }
                                        fixedWidth
                                    />
                                </div>
                                <span>There are currently no trial groups</span>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <InlineLoading absoluteCenter={true} size={50} />
            )}
        </TrialLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialGroupsPageProps> => {
    const trial = !(state.trials.formData instanceof Array)
        ? state.trials.formData
        : undefined;
    const trialGroups = (state.trialGroup.formData instanceof Array)
        ? state.trialGroup.formData
        : undefined;
    const trialInstitutions = (state.trialInstitution.formData instanceof Array)
        ? state.trialInstitution.formData
        : undefined;
    const permissions = state.user.userSummary?.permissions || [];

    return {
        trial: trial!,
        loadingTrial: state.trials.loadState?.status === RequestState.Pending,
        loadTrialSuccess:
            state.trials.loadState?.status === RequestState.Success,
        loadTrialFailure:
            state.trials.loadState?.status === RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        trialGroups: trialGroups!,
        loadingTrialGroups: state.trialGroup.loadState?.status === RequestState.Pending,
        loadTrialGroupsSuccess:
            state.trialGroup.loadState?.status === RequestState.Success,
        loadTrialGroupsFailure:
            state.trialGroup.loadState?.status === RequestState.Failure,
        loadTrialGroupsErrorCode: state.trialGroup.loadState.errorCode,
        trialInstitutions: trialInstitutions!,
        loadingTrialInstitutions: state.trialInstitution.loadState?.status === RequestState.Pending,
        loadTrialInstitutionsSuccess:
            state.trialInstitution.loadState?.status === RequestState.Success,
        loadTrialInstitutionsFailure:
            state.trialInstitution.loadState?.status === RequestState.Failure,
        loadTrialInstitutionsErrorCode: state.trialInstitution.loadState.errorCode,
        groups: state.groups.formFilteredData,
        loadingGroups:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupsSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupsFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupsErrorCode: state.groups.loadState.errorCode,
        savingTrialGroup:
            state.trialGroup.saveState &&
            state.trialGroup.saveState.status == RequestState.Pending,
        saveTrialGroupSuccess:
            state.trialGroup.saveState &&
            state.trialGroup.saveState.status == RequestState.Success,
        saveTrialGroupFailure:
            state.trialGroup.saveState &&
            state.trialGroup.saveState.status == RequestState.Failure,
        user: state.user.data,
        permissions: permissions!,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupsPageProps> => ({
    loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
    clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
    loadTrialGroups: bindActionCreators(TrialGroupActions.LoadTrialGroupsByTrialId, dispatch),
    clearTrialGroups: bindActionCreators(TrialGroupActions.Clear, dispatch),
    saveTrialGroup: bindActionCreators(
        TrialGroupActions.SaveTrialGroup,
        dispatch
    ),
    loadTrialInstitutions: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionsByTrialId, dispatch),
    clearTrialInstitutions: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
    loadGroups: bindActionCreators(GroupActions.LoadGroup, dispatch),
    clearGroups: bindActionCreators(GroupActions.Clear, dispatch),
    setBreadcrumbs: bindActionCreators(
        NavigationActions.SetBreadcrumbs,
        dispatch
    ),
    setDrawerItems: bindActionCreators(
        NavigationActions.SetDrawerItems,
        dispatch
    )
});

export default connect(mapStateToProps, mapDispatchToProps)(TrialGroupsPage);
